import "./TimeResultsCard.css";

const TIME_API_URL = "https://api.ayesearch.com/api/ai/time/";

interface TimeApiResponse {
  answer?: string;
  error?: string;
  timezone?: string;
  timezone_label?: string;
  time_12h?: string;
  date?: string;
  iso?: string;
  location?: string;
  period?: string;
  relation?: string;
  direction?: "past" | "future";
  amount?: number;
  unit?: string;
  elapsed_minutes?: number;
  elapsed_hours?: number;
}

interface ConversionDefinition {
  label: string;
  query: string;
  fallbackQuery?: string;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function browserLocale(): string {
  return navigator.language || "en-US";
}

function compactTimezoneName(value: string): string {
  if (!value) return "Local time";
  return value.replace(/_/g, " ");
}

const FRIENDLY_LOCAL_LOCATIONS: Record<string, string> = {
  "America/Los_Angeles": "Seattle, WA",
  "America/New_York": "New York, NY",
  "America/Chicago": "Chicago, IL",
  "America/Denver": "Denver, CO",
  "America/Phoenix": "Phoenix, AZ",
  "America/Anchorage": "Anchorage, AK",
  "Pacific/Honolulu": "Honolulu, HI",
  "Europe/London": "London, UK",
  "Africa/Lagos": "Lagos, Nigeria",
  "Asia/Tokyo": "Tokyo, Japan",
};

function cardHeading(data: TimeApiResponse): string {
  if (data.location) return data.location;
  const timezone = data.timezone || browserTimezone();
  return FRIENDLY_LOCAL_LOCATIONS[timezone] || compactTimezoneName(timezone);
}

function isRelativeResult(data: TimeApiResponse): boolean {
  return (
    data.period === "relative_future" ||
    data.period === "relative_past" ||
    data.period === "elapsed_since_yesterday_clock"
  );
}

function normalizedUnit(data: TimeApiResponse): string {
  const raw = String(data.unit || "hours").toLowerCase();
  if (raw === "hour" || raw === "hours") return "hours";
  if (raw === "minute" || raw === "minutes") return "minutes";
  if (raw === "day" || raw === "days") return "days";
  return raw;
}

function conversionDefinitions(data: TimeApiResponse): ConversionDefinition[] {
  const amount = Number(data.amount || 12);
  const unit = normalizedUnit(data);
  const singular = amount === 1 ? unit.replace(/s$/, "") : unit;

  return [
    {
      label: `What time is it in ${amount} ${singular} today?`,
      query: `What time is it ${amount} ${singular} from now?`,
    },
    {
      label: `What will the time be in ${amount} ${singular}?`,
      query: `What time will it be ${amount} ${singular} from now?`,
    },
    {
      label: `What is ${amount} ${singular} ago from now?`,
      query: `What time was ${amount} ${singular} ago?`,
    },
    {
      label: "What time is it 12 hours after 8pm?",
      query: "What time is it 12 hours after 8pm?",
      fallbackQuery: "What time is it 12 hours from now?",
    },
  ];
}

function renderEmbeddedCard(data: TimeApiResponse): string {
  const time = escapeHtml(data.time_12h || "");
  const date = escapeHtml(data.date || "");
  const heading = escapeHtml(cardHeading(data));
  const timezoneLabel = escapeHtml(data.timezone_label || data.timezone || "");

  return `
    <section class="aye-time-embedded-card" aria-label="Time conversion result">
      <div class="aye-time-embedded-value">${time}</div>
      <div class="aye-time-embedded-date">
        ${date}${timezoneLabel ? ` <span class="aye-time-zone-inline">(${timezoneLabel})</span>` : ""}
      </div>
      <div class="aye-time-embedded-location">Time in ${heading}</div>
    </section>
  `;
}

function renderConversions(data: TimeApiResponse): string {
  if (!isRelativeResult(data)) return "";

  return `
    <section class="aye-time-conversions" aria-label="Try these conversions">
      <div class="aye-time-conversions-title">Try these conversions</div>
      <div class="aye-time-conversion-list">
        ${conversionDefinitions(data)
          .map(
            (item, index) => `
              <div class="aye-time-conversion-item" data-conversion-index="${index}">
                <button
                  class="aye-time-conversion-toggle"
                  type="button"
                  aria-expanded="false"
                  aria-controls="aye-time-conversion-panel-${index}"
                  data-query="${escapeHtml(item.query)}"
                  data-fallback-query="${escapeHtml(item.fallbackQuery || "")}"
                >
                  <span>${escapeHtml(item.label)}</span>
                  <span class="aye-time-conversion-chevron" aria-hidden="true">⌄</span>
                </button>
                <div
                  class="aye-time-conversion-panel"
                  id="aye-time-conversion-panel-${index}"
                  hidden
                ></div>
              </div>
            `,
          )
          .join("")}
      </div>
    </section>
  `;
}

async function fetchTimeResult(query: string): Promise<TimeApiResponse> {
  const url = new URL(TIME_API_URL);
  url.searchParams.set("q", query);

  const timezone = browserTimezone();
  if (timezone) url.searchParams.set("timezone", timezone);

  url.searchParams.set("locale", browserLocale());
  url.searchParams.set(
    "timezone_offset_minutes",
    String(new Date().getTimezoneOffset()),
  );

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(
      `Time API returned HTTP ${response.status} with non-JSON response`,
    );
  }

  const data = (await response.json()) as TimeApiResponse;
  if (!response.ok || data.error || !data.time_12h) {
    throw new Error(data.error || `Time API returned HTTP ${response.status}`);
  }

  return data;
}

function bindConversionToggles(container: HTMLElement): void {
  const toggles = container.querySelectorAll<HTMLButtonElement>(
    ".aye-time-conversion-toggle",
  );

  toggles.forEach((toggle) => {
    toggle.addEventListener("click", async () => {
      const item = toggle.closest<HTMLElement>(".aye-time-conversion-item");
      const panel = item?.querySelector<HTMLElement>(
        ".aye-time-conversion-panel",
      );
      if (!panel) return;

      const isOpen = toggle.getAttribute("aria-expanded") === "true";

      if (isOpen) {
        toggle.setAttribute("aria-expanded", "false");
        panel.hidden = true;
        return;
      }

      toggle.setAttribute("aria-expanded", "true");
      panel.hidden = false;

      if (panel.dataset.loaded === "true") return;

      panel.innerHTML = `
        <div class="aye-time-conversion-loading">Calculating…</div>
      `;

      const primaryQuery = toggle.dataset.query || "";
      const fallbackQuery = toggle.dataset.fallbackQuery || "";

      try {
        let result: TimeApiResponse;
        try {
          result = await fetchTimeResult(primaryQuery);
        } catch (primaryError) {
          if (!fallbackQuery) throw primaryError;
          result = await fetchTimeResult(fallbackQuery);
        }

        panel.innerHTML = renderEmbeddedCard(result);
        panel.dataset.loaded = "true";
      } catch (error) {
        console.error("AyeSearch inline time conversion failed:", error);
        panel.innerHTML = `
          <div class="aye-time-conversion-error">
            Unable to calculate this conversion right now.
          </div>
        `;
      }
    });
  });
}

function renderResolved(container: HTMLElement, data: TimeApiResponse): void {
  const time = escapeHtml(data.time_12h || "");
  const date = escapeHtml(data.date || "");
  const heading = escapeHtml(cardHeading(data));
  const timezoneLabel = escapeHtml(data.timezone_label || data.timezone || "");
  const relativeRelation =
    isRelativeResult(data) && data.relation
      ? `<div class="aye-time-relation">${escapeHtml(data.relation)}</div>`
      : "";

  container.innerHTML = `
    <div class="aye-time-results-wrap">
      <section class="aye-time-results-card" aria-label="AyeSearch time result">
        <div class="aye-time-value">${time}</div>
        <div class="aye-time-date">
          ${date}${timezoneLabel ? ` <span class="aye-time-zone-inline">(${timezoneLabel})</span>` : ""}
        </div>
        <div class="aye-time-location">Time in ${heading}</div>
        ${relativeRelation}
      </section>

      ${renderConversions(data)}
    </div>
  `;

  if (isRelativeResult(data)) {
    bindConversionToggles(container);
  }
}

export async function renderTimeResultsCard(
  container: HTMLElement,
  query: string,
): Promise<void> {
  container.innerHTML = `
    <section class="aye-time-results-card aye-time-results-loading">
      <div class="aye-time-loading-line"></div>
      <div class="aye-time-loading-clock"></div>
      <div class="aye-time-loading-line short"></div>
    </section>
  `;

  try {
    const data = await fetchTimeResult(query);
    renderResolved(container, data);
  } catch (error) {
    console.error("AyeSearch enhanced time card failed:", error);
    container.innerHTML = `
      <div class="aye-time-error">Unable to load the time right now.</div>
    `;
  }
}
